/**
 * RecordListWidget — a compact "top N records" list behind a metric (e.g. Top
 * Open Risks). Uses the drill-down endpoint (page 1, small page size, sorted)
 * and links each row to its record detail. Scoped by the dashboard context.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useDashboard } from '../context/DashboardContext';
import { useDashboardData } from '../hooks/useDashboardData';
import { fetchDrilldown } from '../api/client';
import { routeBuilderFor, recordLinkId } from '../utils/recordRoutes';

function cell(v) {
  if (v == null || v === '') return '—';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return new Date(v).toLocaleDateString();
  return String(v);
}

export default function RecordListWidget({ title, metric, limit = 6, sort = null, sortDir = 'desc' }) {
  const navigate = useNavigate();
  const { siteId, time, departmentId, dimensions, fyStartMonth, refreshMs } = useDashboard();
  const timeKey = time ? JSON.stringify(time) : '';
  const dimsKey = dimensions ? JSON.stringify(dimensions) : '';

  const { data, loading, error } = useDashboardData(
    (signal) => {
      const body = { metric, page: 1, page_size: limit, sort, sort_dir: sortDir };
      if (siteId) body.site_id = siteId;
      if (departmentId) body.department_id = departmentId;
      if (dimensions && Object.keys(dimensions).length) body.dimensions = dimensions;
      if (time) body.time = time;
      if (fyStartMonth) body.fy_start_month = fyStartMonth;
      return fetchDrilldown(body, { signal });
    },
    [metric, siteId, timeKey, departmentId, dimsKey, limit, sort, sortDir, fyStartMonth],
    { enabled: !!metric, refreshMs },
  );

  const cols = (data?.columns || []).slice(0, 3);
  const rows = data?.rows || [];
  const routeFor = routeBuilderFor(data);

  return (
    <div className="rounded-xl border border-gray-200/80 bg-white p-4 shadow-sm transition-shadow duration-200 hover:shadow-md">
      {title && <h3 className="mb-3 text-sm font-semibold text-gray-800">{title}</h3>}
      {loading && <div className="h-28 animate-pulse rounded bg-gray-100" />}
      {!loading && error && <div className="py-4 text-center text-sm text-red-600">Failed to load</div>}
      {!loading && !error && rows.length === 0 && <div className="py-4 text-center text-sm text-gray-400">Nothing to show</div>}
      {!loading && !error && rows.length > 0 && (
        <ul className="divide-y divide-gray-100">
          {rows.map((r, i) => {
            // No link id means no destination — the row stays inert rather
            // than navigating somewhere that cannot load.
            const linkId = recordLinkId(data, r);
            const clickable = routeFor && linkId;
            return (
              <li
                key={r.id || i}
                className={`flex items-center justify-between gap-3 py-2 text-sm ${clickable ? 'cursor-pointer hover:text-blue-700' : ''}`}
                onClick={clickable ? () => navigate(routeFor(linkId, r)) : undefined}
              >
                <span className="flex min-w-0 flex-1 items-center gap-2">
                  {cols.map((c) => (
                    <span key={c.key} className="truncate">{cell(r[c.key])}</span>
                  ))}
                </span>
                {clickable && <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
